import React, { useState, useEffect, memo, useRef, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Ripple } from "primereact/ripple";
import { classNames } from "primereact/utils";
import { FilterMatchMode } from "primereact/api";
import { InputNumber } from "primereact/inputnumber";

import PopOver from "components/common/PopOver/PopOver";
import DisplaySetting from "components/common/DisplaySetting/DisplaySetting";

interface ColProp {
    field: string;
    header: string;
    editor?: any;
    style?: any;
    sortable: boolean;
}

type Cols = ColProp[];

type Rows = any[];

type SettingListItem = {
    id: string | number;
    name: string;
    onClick: () => void;
};

interface IDataTableComponent {
    rows: Rows;
    cols: Cols;
    title: string;
    paginator?: boolean;
    dataKeyId?: string;
    pageDetails?: any;
    pageIndex?: number;
    getSelectedRowData?: (data: any) => void;
    onPageChanged?: (data: any) => void;
    searchboxfield?: boolean;
    showCustomSearchCmp?: boolean;
    CustomSearchCmp?: any;
    showFilterIcon?: boolean;
    onFilterClick?: () => void;
    showOptionsIcon?: boolean;
    onOptionsClick?: () => void;
    showCopyIcon?: boolean;
    onCopyClick?: () => void;
    onHeaderDropdownChange?: (value: any) => void;
    headerTemplate?: JSX.Element;
    showCustomHeader?: boolean;
    CustomHeaderCmp?: any;
    onSortData?: (value: any) => void;
    settingRef?: any
    onSaveRowEdit?: (data: any, index: any) => void;
    onRowEditValidation?: (data: any) => boolean;
    onRowEditClick?: (data: any, isData: boolean) => boolean;
    onRowEditCancel?: (value: any) => void;
    showroweditor?: boolean;
    settingListItems?: Array<SettingListItem>;
    defaultSelectedRows?: any;
    showCheckbox?: boolean;
    isLazyLoading?: boolean;
    selectedParentRows?: any;
    isSelectionFromParent?: boolean;
    isTemplateMode?: boolean;
    isTemplateFromParent?: boolean;
    templateFromParent?: (rowData: any, options: any) => JSX.Element;
    showExpandedRows?: boolean;
    rowExpandedTemplate?: (rowData: any, options: any) => JSX.Element;
    enableDragSelection?: boolean;
    virtualScrollerOptions?: any;
}

export const DataTableComponent: React.FC<IDataTableComponent> = (props) => {
    const {
        rows = [],
        cols = [],
        title = "",
        paginator = false,
        dataKeyId = "",
        pageDetails = {},
        pageIndex = 1,
        getSelectedRowData = () => { },
        onPageChanged = () => { },
        showCustomHeader = false,
        CustomHeaderCmp = null,
        onSortData = () => { },
        settingRef = () => { },
        onSaveRowEdit = () => { },
        onRowEditValidation = () => true,
        onRowEditClick = () => true,
        onRowEditCancel = () => {},
        showroweditor = false,
        settingListItems = [],
        defaultSelectedRows = [],
        showCheckbox = false,
        isLazyLoading = true,
        selectedParentRows = [],
        isSelectionFromParent = false,
        isTemplateMode = false,
        isTemplateFromParent = false,
        templateFromParent,
        showExpandedRows= false,
        rowExpandedTemplate,
        enableDragSelection = true,
        virtualScrollerOptions,
    } = props;

    const [rowData, setRowData] = useState<Array<any>>([]);
    const [tableColumns, setTableColumns] = useState<Array<any>>(cols);
    const [filteredItemsExist, setFilteredItemsExist] = useState<boolean>(false);
    const [unselectedItems, setUnselectedItems] = useState<Array<any>>();
    const [totalRows, setTotalRows] = useState(100);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField] = useState<any>();
    const [sortOrder, setSortOrder] = useState(null);
    const [firstPage, setFirstPage] = useState(0);
    const [lastPage, setLastPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInputTooltip, setPageInputTooltip] = useState("Press 'Enter' key to go to this page.");
    const [selectedRows, setSelectedRows] = useState<Array<any>>([]);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [showDisplaySetting, setShowDisplaySetting] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<any>({});
    const [editingRows, setEditingRows] = useState({});
    const dragAnchorIndexRef = useRef<number | null>(null);
    const selectionAnchorIndexRef = useRef<number | null>(null);
    const isVirtualDragActiveRef = useRef<boolean>(false);
    const hasVirtualDraggedRef = useRef<boolean>(false);

    const isVirtualSelectionMode = Boolean(virtualScrollerOptions);

    // Controls that own their own mouse/focus behaviour, such as the inline row editors. Row
    // selection must leave clicks on these alone or the control never receives focus.
    function isInteractiveElement(element: HTMLElement | null) {
        return Boolean(element?.closest("input, textarea, select, button, a, .p-dropdown, .p-calendar, .p-checkbox, .p-inputtext, [contenteditable='true']"));
    }

    const getRowKey = useCallback((item: any) => {
        if (!item) {
            return undefined;
        }

        return dataKeyId ? item?.[dataKeyId] : item;
    }, [dataKeyId]);

    const getRowIndexByKey = useCallback((key: any) => {
        if (key === undefined || key === null) {
            return -1;
        }

        return rowData.findIndex((item) => getRowKey(item) === key);
    }, [rowData, getRowKey]);

    const updateSelection = useCallback((nextSelection: Array<any>) => {
        setSelectedRows(nextSelection);
        getSelectedRowData(nextSelection);
    }, [getSelectedRowData]);

    const getTranslateYFromTransform = useCallback((transformValue: string) => {
        if (!transformValue || transformValue === "none") {
            return 0;
        }

        const matrix3dMatch = transformValue.match(/matrix3d\(([^)]+)\)/);
        if (matrix3dMatch?.[1]) {
            const matrixValues = matrix3dMatch[1].split(",").map((value) => Number(value.trim()));
            return Number.isFinite(matrixValues[13]) ? matrixValues[13] : 0;
        }

        const matrixMatch = transformValue.match(/matrix\(([^)]+)\)/);
        if (matrixMatch?.[1]) {
            const matrixValues = matrixMatch[1].split(",").map((value) => Number(value.trim()));
            return Number.isFinite(matrixValues[5]) ? matrixValues[5] : 0;
        }

        const translateYMatch = transformValue.match(/translateY\(([-\d.]+)px\)/);
        if (translateYMatch?.[1]) {
            const translateY = Number(translateYMatch[1]);
            return Number.isFinite(translateY) ? translateY : 0;
        }

        const translate3dMatch = transformValue.match(/translate3d\([^,]+,\s*([-\d.]+)px,/);
        if (translate3dMatch?.[1]) {
            const translateY = Number(translate3dMatch[1]);
            return Number.isFinite(translateY) ? translateY : 0;
        }

        return 0;
    }, []);

    const getRowIndexFromElement = useCallback((rowElement: HTMLTableRowElement | null) => {
        if (!rowElement) {
            return -1;
        }

        const rowContainer = rowElement.parentElement;
        if (!rowContainer) {
            return -1;
        }

        const renderedRows = Array.from(rowContainer.querySelectorAll("tr.p-selectable-row"));
        const renderedIndex = renderedRows.indexOf(rowElement);
        if (renderedIndex < 0) {
            return -1;
        }

        if (!isVirtualSelectionMode) {
            return renderedIndex;
        }

        const itemSize = Number(virtualScrollerOptions?.itemSize ?? 0);
        if (!itemSize) {
            return renderedIndex;
        }

        const tableRoot = rowElement.closest(".p-datatable") as HTMLElement | null;
        const virtualContent = (tableRoot?.querySelector(".p-virtualscroller-content") as HTMLElement | null) ||
            (rowContainer as HTMLElement);
        const transformStyle = virtualContent?.style?.transform || window.getComputedStyle(virtualContent).transform;
        const offsetY = getTranslateYFromTransform(transformStyle || "");
        const virtualStartIndex = Math.max(0, Math.round(offsetY / itemSize));

        return virtualStartIndex + renderedIndex;
    }, [getTranslateYFromTransform, isVirtualSelectionMode, virtualScrollerOptions]);

    const selectRangeByIndexes = useCallback((startIndex: number, endIndex: number) => {
        if (startIndex < 0 || endIndex < 0 || !rowData?.length) {
            return;
        }

        const lowerIndex = Math.min(startIndex, endIndex);
        const upperIndex = Math.max(startIndex, endIndex);
        const rangedRows = rowData.slice(lowerIndex, upperIndex + 1);
        updateSelection(rangedRows);
    }, [rowData, updateSelection]);

    const getEventRowIndex = useCallback((event: any) => {
        const targetElement = event?.originalEvent?.target as HTMLElement | null;
        const rowElement = targetElement?.closest("tr.p-selectable-row") as HTMLTableRowElement | null;
        const absoluteIndex = getRowIndexFromElement(rowElement);

        if (absoluteIndex >= 0) {
            return absoluteIndex;
        }

        if (typeof event?.index === "number") {
            return event.index;
        }

        if (event?.data) {
            return getRowIndexByKey(getRowKey(event.data));
        }

        return -1;
    }, [getRowIndexByKey, getRowIndexFromElement, getRowKey]);

  
    useEffect(() => {
        if (cols?.length && tableColumns) {      
             const updatedColumns = cols.map((col, index) => {   
             const flexValue = col.style.flex;          
             return {
                    ...col,
                    style: {
                        flex: flexValue,
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        overflow: 'hidden',
                        tableLayout: 'auto',
                        width:'180px',
                        Minwidth:'200px'
                    }
                 };
            });    
            if((cols?.length !== tableColumns?.length) && unselectedItems?.length){
                const filteredColumns = updatedColumns.filter(updatedColumn => 
                    !unselectedItems.some(unselectedItem => unselectedItem.field === updatedColumn.field)
                );
            setTableColumns(filteredColumns);
            }
            else if (!filteredItemsExist){
                setTableColumns(updatedColumns);
            }
        }
        }, [cols, rows]);      

    useEffect(() => {
        if (isSelectionFromParent) {
            setSelectedRows(selectedParentRows);
        }
    }, [selectedParentRows, isSelectionFromParent])

    useEffect(() => {
        setRowData(rows);
        setPageInputTooltip("Press 'Enter' key to go to this page.");
        setCurrentPage(pageIndex + 1);
        setFirstPage(pageIndex * pageDetails?.currentLength);
        let lastPageIndex = Math.ceil(pageDetails?.totalCount / pageDetails?.currentLength);
        setLastPage(lastPageIndex);
        setTotalRecords(pageDetails?.totalCount);
        setTotalRows(pageDetails?.pageCount);
    }, [pageDetails, rows, pageIndex]);

    
    

    const onCustomPage = (event) => {
        setSortField('');
        setFirstPage(event.first);
        setTotalRows(event.rows);
        setCurrentPage(event.page + 1);
        onPageChanged && onPageChanged(event.page);
    };

    const onSort = (event) => {
        setFirstPage(event.first);
        setTotalRows(event.rows);
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
        onSortData && onSortData(event);
    };

    const paginatorTemplate = {
        layout: "PrevPageLink NextPageLink FirstPageLink PageLinks LastPageLink RowsPerPageDropdown CurrentPageReport JumpToPageInput",
        FirstPageLink: (options) => {
            if (pageIndex > 2 && lastPage > 5) {
                return (
                    <button type="button" className={`${options.className} mr-3`} onClick={options.onClick} disabled={options.disabled}>
                        <span className="action-btn">1</span>
                        <Ripple />
                    </button>
                );
            }
        },
        PrevPageLink: (options) => {
            return (
                <button type="button" className={options.className} onClick={options.onClick} disabled={options.disabled}>
                    <span className="action-btn">Previous</span>
                    <Ripple />
                </button>
            );
        },
        NextPageLink: (options) => {
            return (
                <button type="button" className={`${options.className} mr-3`} onClick={options.onClick} disabled={options.disabled}>
                    <span className="action-btn">Next</span>
                    <Ripple />
                </button>
            );
        },
        LastPageLink: (options) => {
            if (pageIndex < lastPage - 3 && lastPage > 5) {
                return (
                    <button type="button" className={`${options.className} mr-3`} onClick={options.onClick} disabled={options.disabled}>
                        <span className="action-btn">{lastPage}</span>
                        <Ripple />
                    </button>
                );
            }
        },
        PageLinks: (options) => {
            if (
                (options.view.startPage === options.page && options.view.startPage !== 0) ||
                (options.view.endPage === options.page && options.page + 1 !== options.totalPages)
            ) {
                const className = classNames(options.className, { "p-disabled": true });

                return (
                    <span className={`${className} mr-3`} style={{ userSelect: "none" }}>
                        ...
                    </span>
                );
            }

            return (
                <button type="button" className={options.className} onClick={options.onClick}>
                    {options.page + 1}
                    <Ripple />
                </button>
            );
        },
        JumpToPageInput: (options) => {
            return null;
        },
        RowsPerPageDropdown: (options) => {
            return null;
        },
        CurrentPageReport: (options) => {
            return (
                <span className="current-page mr-3" style={{ color: "var(--text-color)", userSelect: "none" }}>
                    Go to{" "}
                    <InputNumber
                        size={2}
                        className="ml-1"
                        value={currentPage}
                        tooltip={pageInputTooltip}
                        tooltipOptions={{ position: "left" }}
                        onKeyDown={(e) => onPageInputKeyDown(e, options)}
                        onChange={onPageInputChange}
                    />
                </span>
            );
        },
    };

    const onPageInputKeyDown = (event, options) => {
        if (event.key === "Enter") {
            const page = parseInt(currentPage as any as string);
            if (page < 1 || page > options.totalPages) {
                setPageInputTooltip(`Value must be between 1 and ${options.totalPages}.`);
            } else {
                const first = currentPage ? options.rows * (page - 1) : 0;
                onPageChanged && onPageChanged(page - 1);
                setFirstPage(first);
                setPageInputTooltip("Press 'Enter' key to go to this page.");
            }
        }
    };

    const onPageInputChange = (event) => {
        setCurrentPage(event.value === null ? 0 : event.value);
    };
    const renderHeader = () => {
        return <>{props.headerTemplate}</>;
    };

    const searchHeader = renderHeader();

    useEffect(() => {
        if (defaultSelectedRows && defaultSelectedRows.length > 0) {          
            setSelectedRows(defaultSelectedRows);
            getSelectedRowData(defaultSelectedRows);
        } else {
            if (rowData?.length > 0 && !isSelectionFromParent) {
                if (selectedRows.length === 0) {
                    const firstRowEle = rowData[0];                 
                    setSelectedRows([firstRowEle]);
                    getSelectedRowData([firstRowEle]);
                }
            } else {
                setSelectedRows([]); //To clear previous selected row.
                getSelectedRowData([]); //To clear previous value of selected row.
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData]);

    const onSelectRow = (e: any) => {
        const nextRows = Array.isArray(e?.value) ? e.value.filter((item: any) => item) : [];
        updateSelection(nextRows);
    };

    const onVirtualTableMouseDown = (event: any) => {
        if (!isVirtualSelectionMode || !enableDragSelection || event?.button !== 0) {
            return;
        }

        const targetElement = event?.target as HTMLElement | null;

        // Drag-selection must not start on an editor. preventDefault() below cancels the browser's
        // default focus handling, which left inline-edit inputs with no cursor and swallowed every
        // keystroke - only on lists big enough to turn virtual scrolling on.
        if (isInteractiveElement(targetElement)) {
            return;
        }

        const rowElement = targetElement?.closest("tr.p-selectable-row") as HTMLTableRowElement | null;
        const clickedIndex = getRowIndexFromElement(rowElement);
        if (clickedIndex < 0) {
            return;
        }

        isVirtualDragActiveRef.current = true;
        hasVirtualDraggedRef.current = false;
        dragAnchorIndexRef.current = clickedIndex;
        event?.preventDefault?.();
    };

    const onVirtualTableMouseUp = () => {
        if (!isVirtualSelectionMode || !enableDragSelection) {
            return;
        }

        isVirtualDragActiveRef.current = false;
        dragAnchorIndexRef.current = null;
    };

    const onVirtualRowClick = (event: any) => {
        if (!isVirtualSelectionMode || !event?.data) {
            return;
        }

        // Clicking inside a row's editor must not change the selection out from under the edit.
        if (isInteractiveElement(event?.originalEvent?.target as HTMLElement | null)) {
            return;
        }

        if (hasVirtualDraggedRef.current) {
            hasVirtualDraggedRef.current = false;
            return;
        }

        const clickedRow = event.data;
        const clickedIndex = getEventRowIndex(event);
        const clickedKey = getRowKey(clickedRow);
        const isShiftPressed = Boolean(event?.originalEvent?.shiftKey);
        const isMetaPressed = Boolean(event?.originalEvent?.ctrlKey || event?.originalEvent?.metaKey);

        if (clickedIndex < 0) {
            return;
        }

        if (isShiftPressed) {
            const selectedAnchorIndex = selectedRows?.length
                ? getRowIndexByKey(getRowKey(selectedRows[selectedRows.length - 1]))
                : -1;
            const anchorIndex = selectionAnchorIndexRef.current ?? selectedAnchorIndex;
            const startIndex = anchorIndex >= 0 ? anchorIndex : clickedIndex;
            dragAnchorIndexRef.current = startIndex;
            selectRangeByIndexes(startIndex, clickedIndex);
            return;
        }

        dragAnchorIndexRef.current = clickedIndex;
        selectionAnchorIndexRef.current = clickedIndex;

        if (isMetaPressed) {
            const hasClickedRow = selectedRows.some((row) => getRowKey(row) === clickedKey);
            const nextSelection = hasClickedRow
                ? selectedRows.filter((row) => getRowKey(row) !== clickedKey)
                : [...selectedRows, clickedRow];
            updateSelection(nextSelection);
            return;
        }

        updateSelection([clickedRow]);
    };

    const onVirtualRowMouseEnter = (event: any) => {
        if (!isVirtualSelectionMode || !enableDragSelection || !event?.data || !isVirtualDragActiveRef.current) {
            return;
        }

        const currentIndex = getEventRowIndex(event);
        if (currentIndex < 0) {
            return;
        }

        if (dragAnchorIndexRef.current === null || dragAnchorIndexRef.current < 0) {
            const fallbackAnchor = selectionAnchorIndexRef.current ?? currentIndex;
            dragAnchorIndexRef.current = fallbackAnchor >= 0 ? fallbackAnchor : currentIndex;
        }

        const anchorIndex = dragAnchorIndexRef.current;
        if (anchorIndex === null || anchorIndex < 0) {
            return;
        }

        if (anchorIndex !== currentIndex) {
            hasVirtualDraggedRef.current = true;
        }
        selectRangeByIndexes(anchorIndex, currentIndex);
    };

    useEffect(() => {
        const resetDragAnchor = () => {
            isVirtualDragActiveRef.current = false;
            dragAnchorIndexRef.current = null;
        };

        window.addEventListener("mouseup", resetDragAnchor);
        return () => {
            window.removeEventListener("mouseup", resetDragAnchor);
        };
    }, []);

    useEffect(() => {
        if (!selectedRows?.length) {
            dragAnchorIndexRef.current = null;
            selectionAnchorIndexRef.current = null;
        }
    }, [selectedRows]);

    const onRowEditComplete = (e) => {
        let { newData, index } = e;

        onSaveRowEdit && onSaveRowEdit(newData, index);
    };

    const rowEditValidator = (rowData, options) => {
        let value = false;
        value = onRowEditValidation(rowData);
        return value;
    };

    const onRowEditChange = (e) => {
        if (Object.keys(editingRows).length === 0 || Object.keys(e.data).length === 0) {
            let isEditable = true;
            const isData = Object.keys(e.data).length ? true : false; 
            const findIndex = rowData?.findIndex((item)=> item[dataKeyId] == Object.keys(e.data)[0]);         
            isEditable = onRowEditClick(rowData[findIndex],isData);
            if (isEditable) {
                setEditingRows(e.data);
            }
        }
    };    

    const columnTemplate = (rowData, options) => {
        return (
            <div>{rowData[options.field]}</div>
        )
    }

    const NormalColumns = tableColumns.map(({ field, header, editor, style, sortable, headerClassName, align }) => {
        return (
            <Column
                key={field}
                field={field}
                header={header}
                editor={editor}
                style={style}
                sortable={sortable}
                align={align}
                headerClassName={headerClassName}
            />
        )
    })

    const TemplateColumns = tableColumns.map(({ field, header, editor, style, sortable, headerClassName, align }) => {
        return (
            <Column
                headerTooltip={header}
                headerTooltipOptions={{position:"bottom", style:{fontSize:10}}}
                key={field}
                field={field}
                header={header}
                editor={editor}
                style={style}
                sortable={sortable}
                align={align}
                headerClassName={headerClassName}
                body={isTemplateFromParent ? templateFromParent : columnTemplate}
            />
        )
    })

    const toggleRowExpansion = (e: any) => {
        let eventKeys = Object.keys(e.data);    
        if (eventKeys && eventKeys.length) {
            let item: any = {};
            if ((eventKeys.length === 1) || (!(Object.keys(expandedRows).length))) {
                item[`${eventKeys[0]}`] = true;
            } else {
                let rowsExpanded = Object.keys(expandedRows);
                eventKeys.forEach(element => {
                    if (!(rowsExpanded.includes(element.toString()))) {
                        item[`${element}`] = true;                  
                    }
                });
            }
            setExpandedRows(item);
        } else {
            setExpandedRows({});      
        }
    }

    const virtualRowEventProps: any = isVirtualSelectionMode
        ? {
            onRowMouseEnter: onVirtualRowMouseEnter,
        }
        : {};

    return (
        <>
            <div className="custom-table bg-white">
                {showCustomHeader ? <CustomHeaderCmp /> : <div className="heading">{title}</div>}
                <DataTable
                    value={rowData}
                    size="small"
                    responsiveLayout="scroll"
                    editMode="row"
                    editingRows={editingRows}
                    onRowEditComplete={onRowEditComplete}
                    rowEditValidator={rowEditValidator}
                    onRowEditChange={onRowEditChange}
                    onRowEditCancel={onRowEditCancel}
                    paginator={paginator}
                    paginatorTemplate={paginatorTemplate}
                    first={firstPage}
                    rows={totalRows}
                    onPage={onCustomPage}
                    onSort={onSort}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    selectionMode="multiple"
                    dragSelection={enableDragSelection && !isVirtualSelectionMode}
                    selection={selectedRows}
                    onSelectionChange={!isVirtualSelectionMode ? (e) => onSelectRow(e) : undefined}
                    onRowClick={onVirtualRowClick}
                    onMouseDown={onVirtualTableMouseDown}
                    onMouseUp={onVirtualTableMouseUp}
                    dataKey={dataKeyId}
                    emptyMessage="No data found."
                    header={searchHeader}
                    filters={filters}
                    onFilter={(e) => setFilters(e.filters as any)}
                    scrollDirection="both"
                    scrollable
                    resizableColumns
                    columnResizeMode="expand"
                    showGridlines={false}
                    expandedRows={expandedRows}
                    totalRecords={totalRecords}
                    lazy={isLazyLoading}
                    virtualScrollerOptions={virtualScrollerOptions}
                    onRowToggle={(e) => toggleRowExpansion(e)}
                    rowExpansionTemplate={rowExpandedTemplate}
                    {...virtualRowEventProps}

                >
                    {showroweditor ? <Column rowEditor exportable={false} alignFrozen="left" frozen></Column> : null}
                    {showCheckbox ? (<Column selectionMode="multiple" headerStyle={{}} exportable={false}></Column>) : null}
                    {showExpandedRows ? (<Column expander={true} style={{ width: '5rem' }} />) : null}
                    {isTemplateMode ? TemplateColumns : NormalColumns}
                </DataTable>
            </div>
            <PopOver ref={settingRef}>
                <ul>
                    {settingListItems?.length > 0 &&
                        settingListItems.map((item: SettingListItem) => (
                            <li key={item?.id} role="button" onClick={item?.onClick}>
                                {item?.name}
                            </li>
                        ))}
                    <li role="button" onClick={() => setShowDisplaySetting(true)}>
                        Display Settings
                    </li>
                </ul>
            </PopOver>
            {showDisplaySetting && (<DisplaySetting
                showModal={showDisplaySetting}
                setShowModal={setShowDisplaySetting}
                displayColumns={cols}
                setdisplayColumns={setTableColumns}
                setFilteredItemsExist={setFilteredItemsExist}
                setUnselectedItems={setUnselectedItems}
                selectedCols={tableColumns}
            />)}
        </>
    );
};

export default memo(DataTableComponent);
